import type { NotificationEditEvent } from 'Services/SalesManagement/Features/Notification/notificationSlice'
import { useAppDispatch } from 'Store'
import { dateFormatter } from 'Util/formatters'
import SnackbarUtils from 'Util/Snackbar'
import type ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import type { SelectChangeEvent } from '@mui/material'
import { type AsyncThunkAction, unwrapResult } from '@reduxjs/toolkit'
import { type FormEvent, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

interface BaseNotificationDetailShape {
  id: number | null
  active_from: Date | null
  active_to: Date | null
  type: string
  content_en: string
  content_fi: string
  content_sv: string
  edit_events: NotificationEditEvent[]
}

interface UseNotificationDetailProps<T extends BaseNotificationDetailShape> {
  initialDetails: T
  initialFieldErrors: Record<string, never[]>
  isNewInstance: boolean
  instanceIdParam: number
  previousLocation: string
  createAction: (details: T) => AsyncThunkAction<any, any, any>
  updateAction: (details: T) => AsyncThunkAction<any, any, any>
  deleteAction: (params: { id: number }) => AsyncThunkAction<any, any, any>
  messages: {
    createSuccess: string
    createError: string
    updateSuccess: string
    updateError: string
    deleteSuccess: string
    deleteError: string
  }
}

function useNotificationDetail<T extends BaseNotificationDetailShape>({
  initialDetails,
  initialFieldErrors,
  isNewInstance,
  instanceIdParam,
  previousLocation,
  createAction,
  updateAction,
  deleteAction,
  messages
}: UseNotificationDetailProps<T>) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const [notificationDetails, setNotificationDetails] = useState<T>(initialDetails)

  const [tabValue, setTabValue] = useState('fi')

  const [fieldErrors, setFieldErrors] = useState(initialFieldErrors)

  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isLogDialogOpen, setIsLogDialogOpen] = useState(false)

  const [isDeleteActionLoading, setIsDeleteActionLoading] = useState(false)

  const formRef = useRef<HTMLFormElement>(null)

  const [fiEditor, setFiEditor] = useState<ClassicEditor>()
  const [enEditor, setEnEditor] = useState<ClassicEditor>()
  const [svEditor, setSvEditor] = useState<ClassicEditor>()

  const handleSelectChange = (event: SelectChangeEvent, name: keyof T) => {
    const { value } = event.target
    setNotificationDetails(prevState => ({ ...prevState, [name]: value }))
  }

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    saveEditorContent(tabValue)
    setTabValue(newValue)
  }

  const handleDatePickerChange = (value: Date | null, name: string) => {
    let formattedValue = ''
    if (value instanceof Date) {
      formattedValue = dateFormatter(value).toString()
    }
    setNotificationDetails(prevState => ({ ...prevState, [name]: formattedValue }))
  }

  const handleSubmit = () => {
    formRef.current?.requestSubmit()
  }

  const handleSave = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (isNewInstance) {
      dispatch(createAction(notificationDetails))
        .then(unwrapResult)
        .then(() => {
          setIsSaveDialogOpen(false)
          navigate(previousLocation)
          SnackbarUtils.success(messages.createSuccess)
        })
        .catch(error => {
          if (error) {
            setIsSaveDialogOpen(false)
            setFieldErrors({
              ...error
            })
          }
          SnackbarUtils.error(messages.createError)
        })
    } else {
      dispatch(updateAction(notificationDetails))
        .then(unwrapResult)
        .then(() => {
          setIsSaveDialogOpen(false)
          navigate(previousLocation)
          SnackbarUtils.success(messages.updateSuccess)
        })
        .catch(error => {
          if (error) {
            setIsSaveDialogOpen(false)
            setFieldErrors({
              ...error
            })
          }
          SnackbarUtils.error(messages.updateError)
        })
    }
  }

  const handleDelete = () => {
    setIsDeleteActionLoading(true)
    dispatch(deleteAction({ id: instanceIdParam }))
      .then(unwrapResult)
      .then(() => {
        setIsDeleteDialogOpen(false)
        SnackbarUtils.success(messages.deleteSuccess)
      })
      .catch(() => {
        SnackbarUtils.error(messages.deleteError)
      })
      .finally(() => {
        setIsDeleteActionLoading(false)
        navigate(previousLocation)
      })
  }

  const onSaveClick = () => {
    saveEditorContent(tabValue)
    setIsSaveDialogOpen(true)
  }

  const onDelete = () => {
    setIsDeleteDialogOpen(!isDeleteDialogOpen)
  }

  const viewLog = () => {
    setIsLogDialogOpen(true)
  }

  const closeLog = () => {
    setIsLogDialogOpen(false)
  }

  const saveEditorContent = (lang: string) => {
    const field = `content_${lang}`

    let editor = fiEditor
    if (lang === 'sv') {
      editor = svEditor
    } else if (lang === 'en') {
      editor = enEditor
    }

    // This means that the user has not visited the tab yet
    if (!editor) {
      return
    }

    const content = editor.getData()
    setNotificationDetails(prevState => ({ ...prevState, [field]: content }))
  }

  return {
    // State
    notificationDetails,
    setNotificationDetails,
    tabValue,
    setTabValue,
    fieldErrors,
    setFieldErrors,
    isSaveDialogOpen,
    setIsSaveDialogOpen,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    isDeleteActionLoading,
    setIsDeleteActionLoading,
    isLogDialogOpen,
    setIsLogDialogOpen,
    formRef,

    // Editors
    fiEditor,
    setFiEditor,
    enEditor,
    setEnEditor,
    svEditor,
    setSvEditor,

    // Handlers
    handleTabChange,
    handleSelectChange,
    handleDatePickerChange,
    handleSubmit,
    handleSave,
    handleDelete,
    onSaveClick,
    onDelete,
    viewLog,
    closeLog,
    saveEditorContent
  }
}

export default useNotificationDetail
